<script setup>
/**
 * Imports
 */
import { AsideLinkComponent } from "@/components/layout/aside/components";
import { computed, onMounted, ref } from "vue";
import { usePermissions } from "@/modules/auth/composables/usePermissions";
import { useReturnsStore } from "@/modules/applications/returns.store";

/**
 * Props & Emits
 */

/**
 * Variables
 */
const { hasAccess } = usePermissions();
const returnsStore = useReturnsStore();

const allRoutes = ref([
  {
    name: "main",
    title: "Главная панель",
    icon: "layout-dashboard",
    permission: ["tank:index", "fuel:index", "application:index", "arrival:index"],
  },
  {
    name: "reservoirs",
    title: "Резервуары",
    icon: "cylinder",
    permission: "tank:index",
  },
  {
    title: "Заявки",
    icon: "list",
    expanded: false,
    permission: null, // Проверяется по sublinks
    sublinks: [
      {
        name: "requests",
        title: "Список заявок",
        icon: "list",
        activeNames: ["requests", "request-create", "request-edit", "request-open"],
        permission: "application:index",
      },
      {
        name: "returns",
        title: "Возвраты остатков",
        icon: "undo-2",
        activeNames: ["returns"],
        permission: "application:return_index",
        // Бейдж с количеством открытых возвратов (см. computed routes)
        badgeKey: "returns",
      },
    ],
  },
  {
    title: "Автопарк",
    icon: "car-front",
    expanded: false,
    permission: null, // Проверяется по sublinks
    sublinks: [
      {
        name: "transports",
        title: "ТС",
        icon: "truck",
        activeNames: ["transports", "transport"],
        permission: "transport:index",
      },
      {
        name: "drivers",
        title: "Водители",
        icon: "users",
        activeNames: ["drivers", "driver"],
        permission: "driver:index",
      },
      // {
      //   name: "transport-history",
      //   title: "История эксплуатации",
      //   icon: "list",
      // },
    ],
  },
  {
    name: "arrivals",
    title: "Поступления",
    icon: "package-check",
    permission: "arrival:index",
  },
  {
    name: "purchases",
    title: "Закупки",
    icon: "russian-ruble",
    permission: "purchase:index",
  },
  // {
  //   name: "geography",
  //   title: "География АЗС",
  //   icon: "map",
  // },
  // {
  //   title: "Аналитика",
  //   icon: "chart-no-axes-combined",
  //   expanded: false,
  //   sublinks: [
  //     {
  //       name: "",
  //       title: "Заявок",
  //       icon: "file-chart-column",
  //     },
  //     {
  //       name: "analytics",
  //       title: "Поступлений",
  //       icon: "arrow-big-down-dash",
  //     },
  //   ],
  // },
  // {
  //   name: "document",
  //   title: "Журнал документов",
  //   icon: "files",
  // },
  {
    name: "guide",
    title: "Справочники",
    icon: "circle-help",
    activeNames: ["guide", "fuels", "guide-partners", "users", "roles", "stations"],
    permission: [
      "tank:index",
      "fuel:index",
      "transport:index",
      "driver:index",
      "counterparty:index",
      "user:index",
      "role:index",
      "station:index",
    ],
  },
]);

/**
 * Computed
 */
// Значения бейджей по ключу (badgeKey) — открытые возвраты остатков
const badges = computed(() => ({
  returns: returnsStore.openCount,
}));

const withBadge = (route) =>
  route.badgeKey ? { ...route, badge: badges.value[route.badgeKey] || 0 } : route;

const routes = computed(() => {
  return allRoutes.value
    .map((route) => {
      // Если есть sublinks, фильтруем их
      if (route.sublinks) {
        const filteredSublinks = route.sublinks
          .filter((sublink) => {
            return !sublink.permission || hasAccess(sublink.permission);
          })
          .map(withBadge);

        // Если нет доступных sublinks, скрываем весь раздел
        if (filteredSublinks.length === 0) {
          return null;
        }

        return {
          ...route,
          sublinks: filteredSublinks,
        };
      }

      // Для обычных роутов проверяем permission
      if (route.permission && !hasAccess(route.permission)) {
        return null;
      }

      return withBadge(route);
    })
    .filter((route) => route !== null);
});

/**
 * Hooks
 */
onMounted(() => {
  if (hasAccess("application:return_index")) {
    returnsStore.fetchOpenCount();
  }
});

/**
 * Methods
 */
function toggle(i) {
  const route = routes.value[i];
  if (route && route.sublinks) {
    // Переключаем expanded в исходном массиве
    const originalRoute = allRoutes.value.find((r) => r.title === route.title);
    if (originalRoute) {
      originalRoute.expanded = !originalRoute.expanded;
    }
  }
}
</script>

<template>
  <div class="aside__links">
    <template v-for="(item, i) in routes" :key="i">
      <AsideLinkComponent :data="item" @toggle="toggle(i)" />
      <transition name="submenu-fade">
        <div class="aside__sublinks" v-show="item.expanded">
          <AsideLinkComponent v-for="(sub, ind) in item.sublinks" :key="ind" :data="sub" sublink />
        </div>
      </transition>
    </template>
  </div>
</template>

<style lang="stylus">
.aside {
	&__links {
		display flex
		flex-direction column
		gap: 10px
		padding: 10px
		border-radius 12px
		background var(--white)
		overflow hidden
	}

	&__sublinks {
		display flex
		flex-direction column
		gap: 5px
	}
}
</style>
